import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import get from 'lodash.get';
import React, { useEffect, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { SkeletonLoadingConfig } from '../global/globalSettings';
import { isArray } from '../helpers/typeOf';
import { Cloner } from '../hooks/Cloner';
import GenContextualMenu from './GenContextualMenu';

const PREFIX = 'GenericTable';

const classes = {
  paper: `${PREFIX}-paper`,
};
const StyledDiv = styled('div')(({ theme }) => ({
  [`& .${classes.paper}`]: {
    boxShadow: 'none',
    borderRadius: '4px',
    border: `1px solid ${theme.palette.divider}`,
    width: '100%',
    overflow: 'hidden',
    marginTop: '16px',
  },
}));

const GenTables = ({
  data,
  columns,
  rowClick,
  onLoadMore,
  tableContainer,
  searchStatus = null,
  where,
  sx,
  delimiter = ' - ',
  optionsGetter,
  isLoading,
  hasMore,
  tableHeadSx = { backgroundColor: 'white' },
  rowSXRules = () => {},
}) => {
  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore,
    disabled: isLoading,
    onLoadMore,
  });
  const [rows, setRows] = useState(data);
  const [, setSearch] = useState(null);

  useEffect(() => {
    data && setRows(data);
    searchStatus && setSearch(searchStatus);
  }, [columns, data, searchStatus]);

  const getOptionsMapped = row => {
    const options = optionsGetter(row);

    return (options || []).map(option => ({
      ...option,
      onClick: () => {
        (option.onClick || (() => {}))(row);
      },
    }));
  };

  const valueToOneComponent = (component, valOneComponent) =>
    component ? Cloner(component, valOneComponent) : valOneComponent;

  const valueToTwoComponents = (component, valTwoComponent) =>
    component ? Cloner(component, valTwoComponent) : valTwoComponent.join(',');

  const tableCellComponents = (component, valTwoComponent, valOneComponent) =>
    !valTwoComponent.length
      ? valueToOneComponent(component, valOneComponent)
      : valueToTwoComponents(component, valTwoComponent);

  return columns ? (
    <StyledDiv>
      <Paper className={classes.paper} sx={sx}>
        <TableContainer sx={tableContainer} ref={rootRef}>
          <Table stickyHeader>
            <TableHead sx={tableHeadSx}>
              <TableRow>
                {columns.map(col => {
                  return (
                    <TableCell
                      key={col.id}
                      align={col.align}
                      style={{
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        width: col.width,
                      }}
                      sx={{
                        ...(col?.sx ? col.sx : {}),
                      }}
                    >
                      {col.labelComponent
                        ? Cloner(col.labelComponent, col.label)
                        : col.label}
                    </TableCell>
                  );
                })}
                {optionsGetter && (
                  <TableCell sx={{ padding: 0 }} key="options" />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row, rowIndex) => {
                return (
                  <TableRow
                    hover
                    role="div"
                    key={rowIndex}
                    sx={{
                      ':hover': { cursor: rowClick && 'pointer' },
                      ...rowSXRules(row),
                    }}
                    onClick={rowClick ? () => rowClick(row) : undefined}
                    ref={sentryRef}
                  >
                    {columns.map((col, columnIndex) => {
                      let valuesToOneComponent = [];
                      const valuesToTwoComponents = [];
                      let obj;
                      col?.dataSource.forEach(d => {
                        if (isArray(d)) {
                          d.forEach(e => {
                            valuesToTwoComponents.push(row[e]);
                          });
                        } else {
                          const val =
                            get(row, d, col?.defaultValue || d) ??
                            (col?.defaultValue || d);
                          if (val && !isArray(val)) {
                            valuesToOneComponent.push(val);
                          } else {
                            obj = val;
                          }
                        }
                      });
                      if (obj || obj === 0) {
                        valuesToOneComponent = obj;
                      } else if (valuesToOneComponent[0] === delimiter) {
                        valuesToOneComponent.shift();
                        valuesToOneComponent = valuesToOneComponent.join('');
                      } else {
                        valuesToOneComponent = valuesToOneComponent.join('');
                      }
                      return (
                        <TableCell
                          key={rowIndex + columnIndex}
                          align={col?.align}
                          sx={{
                            ...(col?.sx ? col.sx : {}),
                          }}
                        >
                          {tableCellComponents(
                            col.component,
                            valuesToTwoComponents,
                            valuesToOneComponent
                          )}
                        </TableCell>
                      );
                    })}
                    {optionsGetter && (
                      <TableCell
                        sx={{ padding: 0 }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <GenContextualMenu options={getOptionsMapped(row)} />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {(isLoading || hasMore) &&
                [...Array(SkeletonLoadingConfig.amount)].map((_element, k) => (
                  <TableRow hover role="div" key={k}>
                    {columns.map((c, l) => (
                      <TableCell key={k + l}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </StyledDiv>
  ) : null;
};

export default GenTables;
